import { Component } from 'react';

import {
  PRELIMINARY_RCA_TITLE,
  QUESTION_OUTCOME_LABELS,
  RCA_CONTRIBUTING_FACTOR_CATEGORY_LABELS,
  RCA_SUGGESTED_CATEGORY_LABELS,
  describeQuestionDeflectionReason,
  feedbackEntryTypeLabel,
  feedbackSubmissionChannelLabel,
} from '@dha-nexus/shared';

import { describeProvenance } from '../feedbackIntake/RcaReviewSection.jsx';

// Type-specific reviewer detail for a governed record that came from a typed
// intake (commit 6).
//
// FOUR RULES THIS FILE EXISTS TO HOLD
//
// 1. Nothing here renders for a screenshot-feedback record. Every section is
//    gated on the record's `entryType`, and the top-level component returns null
//    for `screen_feedback` — the queue a reviewer knew before this commit is
//    unchanged for the records it already held.
// 2. Delivery language is never composed here. `supportDelivery.delivery`
//    arrives with the server's own `statusLabel`/`statusDetail` from the shared
//    Commit 2 chokepoint, which refuses to claim provider acceptance without a
//    provider delivery id. There is no branch in this file that can print
//    "Sent".
// 3. The preliminary analysis is labelled with the shared title constant,
//    verbatim, and carries the same provenance disclosure the reporter saw. A
//    reviewer must not be able to read a seeded or fallback draft as a finding.
// 4. Every state is TEXT plus a glyph, never colour alone.

// A mirrored, repaired, or schema-drifted record can carry a truthy non-array
// where this surface expects a list. Reading `.length` off such a value passes
// the render guard and `.map()` then throws — so every stored collection goes
// through this normalizer before it is counted or iterated.
function asArray(value) {
  return Array.isArray(value) ? value : [];
}

// Missing provenance is an UNKNOWN fact, not a reassurance. Only an explicit
// `false` may read as "No" — the same `=== false` rule the deflection policy
// applies before it will trust a provenance at all.
const PROVENANCE_UNKNOWN = 'Unknown — provider provenance incomplete';

function fallbackTriState(provenance) {
  if (provenance?.fallbackOccurred === true) return 'Yes';
  if (provenance?.fallbackOccurred === false) return 'No';
  return PROVENANCE_UNKNOWN;
}

function demoGroundingTriState(provenance) {
  if (provenance?.seeded === true || provenance?.illustrative === true) return 'Yes';
  if (provenance?.seeded === false && provenance?.illustrative === false) return 'No';
  return PROVENANCE_UNKNOWN;
}

// A defect in ONE record's stored data must degrade that record's panel, not
// unmount the queue. Each typed section renders inside this boundary; the
// caller keys it by record id so a new selection gets a fresh try.
class TypedIntakeSectionBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) {
      return (
        <article
          className="feedback-ops-panel"
          role="note"
          data-testid="feedback-typed-detail-error"
        >
          <div className="eyebrow">Record detail</div>
          <h3 className="card-title">Details unavailable</h3>
          <p className="muted">
            PRIME could not render this part of the record because its stored data does not match
            what the reviewer surface expects. The rest of the queue is unaffected — review the
            record&apos;s source data before acting on it.
          </p>
        </article>
      );
    }
    return this.props.children;
  }
}

function Line({ label, value, testId }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div>
      <span className="eyebrow">{label}</span>
      <strong data-testid={testId}>{value}</strong>
    </div>
  );
}

function TextList({ label, items, testId }) {
  const values = (Array.isArray(items) ? items : []).filter(Boolean);
  if (!values.length) return null;
  return (
    <section>
      <strong>{label}</strong>
      <ul data-testid={testId}>
        {values.map((value, index) => (
          <li key={`${testId}-${index}`}>{value}</li>
        ))}
      </ul>
    </section>
  );
}

// The submitted OUTCOME of a question, from the shared question vocabulary
// (audit P2-2). `not_applicable` is a STORAGE placeholder — every record carries
// a deflection outcome column, and only a question can have a real one — so it is
// omitted rather than shown: a reviewer reading "Not applicable" on a support
// ticket would reasonably assume a decision was recorded.
function deflectionOutcomeLabel(outcome) {
  if (!outcome || outcome === 'not_applicable') return null;
  // An unrecognized outcome is echoed raw, the same rule the entry-type labels
  // follow: a value PRIME does not know about is a data problem to surface.
  // Own-property lookup only: '__proto__'/'constructor'/'toString' must echo as
  // strings, not resolve inherited objects that break the reviewer detail render.
  if (Object.hasOwn(QUESTION_OUTCOME_LABELS, outcome)) return QUESTION_OUTCOME_LABELS[outcome];
  return String(outcome);
}

/**
 * The canonical chain record → intake.
 *
 * Rendered as a REFERENCE, not a link. A System Admin reviewer CAN read a
 * submitted intake (`getIntakeForActor` allows it), so the old "stays private /
 * would hand you a 403" copy was simply false — the reason there is no link is
 * that the reporter's intake pages are authoring surfaces for a draft, and the
 * review work belongs in the governed queue. The filter beside it is the
 * reviewer's actual route into "everything that came from this request", and it
 * survives conversion — a converted record keeps its `sourceIntakeId`, so this
 * reference does too.
 */
export function SourceIntakeSection({ item, onFilterBySourceIntake }) {
  if (!item?.sourceIntakeId) return null;
  return (
    <article className="feedback-ops-panel" data-testid="feedback-source-intake">
      <div className="eyebrow">Traceability</div>
      <h3 className="card-title">Originating request</h3>
      <div className="feedback-ops-detail-grid">
        <Line
          label="Entry type"
          value={feedbackEntryTypeLabel(item.entryType)}
          testId="feedback-entry-type"
        />
        <Line
          label="Intake reference"
          value={item.sourceIntakeId}
          testId="feedback-source-intake-id"
        />
        <Line
          label="Submission channel"
          value={feedbackSubmissionChannelLabel(item.submissionChannel)}
          testId="feedback-submission-channel"
        />
        <Line
          label="Deflection outcome"
          value={deflectionOutcomeLabel(item.deflectionOutcome)}
          testId="feedback-deflection-outcome"
        />
      </div>
      <p className="muted">
        This governed record is the reviewable copy of that request. Keep the review here in the
        governed queue, and use the filter below to see every governed record that came from it.
      </p>
      {onFilterBySourceIntake ? (
        <button
          type="button"
          className="btn outline sm"
          data-testid="feedback-filter-by-source-intake"
          onClick={() => onFilterBySourceIntake(item.sourceIntakeId)}
        >
          Filter the queue to this request
        </button>
      ) : null}
    </article>
  );
}

/**
 * What the MHS Service Desk would receive, read from the stored dispatch
 * snapshot the submit transaction wrote — never re-rendered here.
 */
export function SupportTicketDetailSection({ item }) {
  if (item?.entryType !== 'support_ticket') return null;
  // AVAILABILITY IS ITS OWN STATE (audit P1-3). "PRIME does not have the delivery
  // projection" and "the source confirmed nothing was prepared" are different
  // facts, and they used to share the same rendering branch. The detail response
  // distinguishes them by PRESENCE: a local record always carries the
  // `supportDelivery` property (null when there is genuinely no dispatch), while
  // a MIRRORED record carries none at all — the stored artifact is deliberately
  // not replicated into the central aggregate. Reading absence as "no handoff was
  // ever prepared" is how the same ticket gets worked twice.
  const hasDeliveryProjection = Object.prototype.hasOwnProperty.call(item, 'supportDelivery');
  const supportDelivery = item.supportDelivery || null;
  const delivery = supportDelivery?.delivery || null;
  const artifact = supportDelivery?.artifact || null;

  return (
    <article className="feedback-ops-panel" data-testid="feedback-support-ticket-detail">
      <div className="eyebrow">Support ticket</div>
      <h3 className="card-title">Service desk handoff</h3>

      {/* PROMINENT and not colour-only: a bordered note with an explicit glyph
          and a full sentence. A reviewer who misses this would work the ticket
          without knowing protected data is still needed to close it. */}
      {artifact?.protectedInformationRequired ? (
        <div
          className="support-ticket-alert tone-warn"
          role="note"
          data-testid="feedback-protected-followup"
        >
          {/* The '!' glyph comes from `.support-ticket-alert.tone-warn h4::before`
              (feedbackIntake.css), which commit 7's visual audit extended to h4 so
              the reviewer's copy of this banner matches the reporter's. Rendering it
              here as well printed it twice. */}
          <h4>Protected information is required through the approved follow-up process</h4>
          <p>
            The reporter marked this ticket as needing CUI, PHI, or PII to resolve. None of it is in
            the ticket text below — it must be exchanged through the approved follow-up process, not
            through this record.
          </p>
        </div>
      ) : null}

      {delivery ? (
        <div className="feedback-ops-detail-grid">
          {/* The server's own label. This component never derives it. */}
          <Line
            label="Delivery state"
            value={delivery.statusLabel}
            testId="feedback-delivery-status"
          />
          <Line label="Delivery mode" value={delivery.mode} />
          <Line
            label="Confirmed by provider"
            value={delivery.deliveredExternally ? 'Yes' : 'No'}
            testId="feedback-delivery-confirmed"
          />
          <Line label="Provider delivery id" value={delivery.providerDeliveryId} />
          <Line label="Delivery attempts" value={String(delivery.attemptCount ?? 0)} />
          <Line label="Last delivery error" value={delivery.lastError} />
        </div>
      ) : null}
      {delivery?.statusDetail ? (
        <p className="muted" data-testid="feedback-delivery-detail">
          {delivery.statusDetail}
        </p>
      ) : null}

      {artifact ? (
        <>
          <div className="feedback-ops-detail-grid">
            <Line label="Subject" value={artifact.subject} testId="feedback-gsc-subject" />
            {artifact.recipients?.recipientPosture === 'user_addressed_draft' ? null : (
              <Line
                label="Addressed to"
                value={[
                  ...asArray(artifact.recipients?.to),
                  ...asArray(artifact.recipients?.cc),
                ].join(', ')}
                testId="feedback-gsc-recipients"
              />
            )}
            <Line label="Artifact version" value={String(artifact.renderedArtifactVersion || 0)} />
          </div>
          {artifact.recipients?.recipientPosture === 'user_addressed_draft' ? (
            <p className="muted" data-testid="feedback-gsc-recipient-guidance">
              Add recipients in your approved mail client. PRIME did not select recipients, send
              this message, or verify delivery.
            </p>
          ) : (
            <p className="muted">
              This is the exact message stored when the ticket was sent for delivery, not a fresh
              render — recipients are the ones resolved at that time.
            </p>
          )}
          <pre className="feedback-ops-artifact" data-testid="feedback-gsc-body">
            {artifact.body}
          </pre>
        </>
      ) : hasDeliveryProjection ? (
        <p className="muted" data-testid="feedback-gsc-absent">
          No outbound message was prepared for this ticket, so there is nothing to show. PRIME
          recorded the request here for the product team.
        </p>
      ) : (
        <p className="muted" data-testid="feedback-gsc-unavailable">
          Delivery details were not replicated from the source environment, so PRIME cannot say
          whether a service-desk message was prepared for this ticket. Review the source record
          before preparing another handoff.
        </p>
      )}
    </article>
  );
}

/**
 * The reporter's confirmed problem analysis, read-only.
 *
 * Labelled with the shared title constant so it reads as the hypothesis it is,
 * and carrying the SAME provenance disclosure the reporter confirmed against.
 */
export function FeatureRequestDetailSection({ item }) {
  if (item?.entryType !== 'feature_service_request') return null;
  const snapshot = item.intakeSnapshot || null;
  const rca = snapshot?.preliminaryRca || null;
  const content = rca?.content || null;
  const transcript = asArray(snapshot?.transcript);
  // Resolves a hypothesis's `sourceTurnRefs` (message ids) to the transcript
  // snapshot rendered below, so a reviewer can verify a claim against the exact
  // turn it cites without leaving the governed record.
  const turnById = new Map(transcript.filter((turn) => turn?.id).map((turn) => [turn.id, turn]));
  const focusTranscriptTurn = (turnId) => {
    const target = document.getElementById(`feedback-turn-${turnId}`);
    if (!target) return;
    target.scrollIntoView?.({ block: 'center' });
    target.focus();
  };

  return (
    <article className="feedback-ops-panel" data-testid="feedback-feature-request-detail">
      <div className="eyebrow">Feature or service request</div>
      <h3 className="card-title">{PRELIMINARY_RCA_TITLE}</h3>

      {rca ? (
        (() => {
          const provenanceCopy = describeProvenance({
            source: rca.source,
            provenance: rca.provenance,
          });
          return (
            <div
              className={`support-ticket-alert ${provenanceCopy.tone}`}
              role="note"
              data-testid="feedback-rca-provenance"
            >
              {/* Glyph from the tone class, once. See RcaReviewSection for why the
                  local span is gone (typed-intake commit 7 visual audit). */}
              <h4>{provenanceCopy.heading}</h4>
              <p>{provenanceCopy.body}</p>
              {provenanceCopy.disclosures?.length ? (
                <ul data-testid="feedback-rca-provenance-disclosures">
                  {provenanceCopy.disclosures.map((disclosure) => (
                    <li key={disclosure}>{disclosure}</li>
                  ))}
                </ul>
              ) : null}
              <p>
                The reporter confirmed this as a working hypothesis
                {rca.approvedAt ? ` on ${new Date(rca.approvedAt).toLocaleString()}` : ''}. PRIME
                has not established any cause.
              </p>
            </div>
          );
        })()
      ) : (
        <p className="muted" data-testid="feedback-rca-absent">
          This request carries no confirmed analysis.
        </p>
      )}

      {content ? (
        <div className="feedback-ops-context" data-testid="feedback-rca-content">
          <section>
            <strong>Problem statement</strong>
            <p data-testid="feedback-rca-problem-statement">{content.problemStatement}</p>
          </section>
          <section>
            <strong>Desired outcome</strong>
            <p>{content.desiredOutcome}</p>
          </section>
          <TextList
            label="Observed symptoms"
            items={content.observedSymptoms}
            testId="feedback-rca-symptoms"
          />
          <TextList label="Known facts" items={content.knownFacts} testId="feedback-rca-facts" />
          <TextList
            label="Assumptions"
            items={content.assumptions}
            testId="feedback-rca-assumptions"
          />
          <TextList
            label="Missing information"
            items={content.missingInformation}
            testId="feedback-rca-missing"
          />
          <TextList
            label="Affected capabilities"
            items={content.affectedCapabilities}
            testId="feedback-rca-capabilities"
          />
          {asArray(content.possibleContributingFactors).length ? (
            <section>
              <strong>Possible contributing factors</strong>
              <ul data-testid="feedback-rca-factors">
                {asArray(content.possibleContributingFactors).map((factor, index) => (
                  <li key={`factor-${index}`}>
                    {RCA_CONTRIBUTING_FACTOR_CATEGORY_LABELS[factor?.category] ||
                      factor?.category ||
                      'Uncategorized'}
                    :{' '}
                    {factor?.statement ||
                      factor?.description ||
                      (typeof factor === 'string' ? factor : 'No description provided')}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {asArray(content.hypotheses).length ? (
            <section>
              <strong>Explanations the reporter confirmed as possible</strong>
              <ol data-testid="feedback-rca-hypotheses">
                {asArray(content.hypotheses).map((hypothesis, index) => (
                  <li key={hypothesis?.id || `hypothesis-${index}`}>
                    <p>{hypothesis?.statement}</p>
                    <TextList
                      label="Supporting"
                      items={hypothesis?.supportingEvidence}
                      testId={`feedback-rca-hypothesis-${index}-supporting`}
                    />
                    <TextList
                      label="Contradicting"
                      items={hypothesis?.contradictingEvidence}
                      testId={`feedback-rca-hypothesis-${index}-contradicting`}
                    />
                    {asArray(hypothesis?.sourceTurnRefs).length ? (
                      <p
                        className="muted feedback-rca-turn-refs"
                        data-testid={`feedback-rca-hypothesis-${index}-turn-refs`}
                      >
                        Drawn from:{' '}
                        {asArray(hypothesis.sourceTurnRefs).map((ref) => {
                          const turn = turnById.get(ref);
                          // A reference that names no turn in this snapshot is a
                          // data problem to SURFACE, not a dead control to offer.
                          if (!turn) {
                            return (
                              <span key={String(ref)}>
                                a turn not in this snapshot ({String(ref)})
                              </span>
                            );
                          }
                          return (
                            <button
                              key={String(ref)}
                              type="button"
                              className="btn outline sm"
                              onClick={() => focusTranscriptTurn(turn.id)}
                            >
                              Turn {turn.sequenceNumber}
                            </button>
                          );
                        })}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ol>
            </section>
          ) : null}
          <div className="feedback-ops-detail-grid">
            <Line label="Current workaround" value={content.currentWorkaround} />
            <Line label="Impact" value={content.impact} />
            <Line
              label="Suggested routing (advisory)"
              value={RCA_SUGGESTED_CATEGORY_LABELS[content.suggestedCategory] || null}
            />
            <Line label="Recommended next action" value={content.recommendedNextAction} />
            {/* Derived by PRIME from the content — deliberately NOT something a
                model or a reporter can talk up. */}
            <Line label="Derived confidence" value={content.confidence} />
          </div>
        </div>
      ) : null}

      {transcript.length ? (
        <section data-testid="feedback-conversation-turns">
          <strong>Conversation turns behind this request</strong>
          {/* REFERENCES, not the text. The reporter's words live on their intake;
              copying them here would duplicate reporter content into a second
              governed surface with its own export paths (§8.18). */}
          <ol className="feedback-ops-history-list">
            {transcript.map((turn) => (
              <li
                key={turn.id || `${turn.sequenceNumber}`}
                id={turn.id ? `feedback-turn-${turn.id}` : undefined}
                data-testid={turn.id ? `feedback-turn-${turn.id}` : undefined}
                // Focusable TARGET only — reached from a hypothesis's source-turn
                // control, never part of the tab order on its own.
                tabIndex={-1}
              >
                <strong>
                  Turn {turn.sequenceNumber} · {turn.actorType === 'user' ? 'Reporter' : 'PRIME'}
                </strong>
                {turn.topic ? ` · ${turn.topic}` : ''}
                {turn.source
                  ? ` · asked by ${turn.source === 'ai' ? 'a model' : 'PRIME’s guide'}`
                  : ''}
                {turn.createdAt ? ` · ${new Date(turn.createdAt).toLocaleString()}` : ''}
                <p className="muted">
                  {turn.contentLength} characters. Open the reporter&apos;s request to read it.
                </p>
              </li>
            ))}
          </ol>
        </section>
      ) : null}
    </article>
  );
}

/**
 * Everything PRIME tried before a person was asked.
 *
 * Without it a reviewer cannot tell "PRIME has no material on this" from "PRIME
 * had material and the reporter wanted a person anyway" — the difference between
 * a documentation gap and a support request.
 */
export function QuestionDetailSection({ item }) {
  if (item?.entryType !== 'general_question') return null;
  const answer = item.intakeSnapshot?.questionAnswer || null;
  const citations = Array.isArray(answer?.citations) ? answer.citations : [];
  const reasons = Array.isArray(answer?.reasons) ? answer.reasons : [];
  const outcome = item.deflectionOutcome || answer?.outcome || null;

  return (
    <article className="feedback-ops-panel" data-testid="feedback-question-detail">
      <div className="eyebrow">Question</div>
      <h3 className="card-title">What PRIME tried before asking a person</h3>

      {answer ? (
        <>
          <div className="feedback-ops-context">
            <section>
              <strong>The question as it was asked</strong>
              <p data-testid="feedback-question-text">{answer.question}</p>
            </section>
            <section>
              <strong>What PRIME searched on</strong>
              {/* Sanitized server-side by the Commit 2 detectors: anything that
                  looked like protected data was masked before retrieval. */}
              <p data-testid="feedback-question-search-terms">{answer.sanitizedSearchTerms}</p>
            </section>
          </div>

          <div className="feedback-ops-detail-grid">
            {/* The one label that says who answered, from the shared chokepoint. */}
            <Line
              label="Outcome"
              value={QUESTION_OUTCOME_LABELS[outcome] || outcome}
              testId="feedback-question-outcome"
            />
            <Line
              label="Retrieval mode"
              value={answer.retrieval?.mode}
              testId="feedback-question-retrieval-mode"
            />
            <Line label="Retrieval completed" value={answer.retrieval?.completed ? 'Yes' : 'No'} />
            <Line label="Retrieval note" value={answer.retrieval?.reason} />
            {/* Chunks and documents are different counts: two passages of one
                file are ONE source. Labels must never conflate them. */}
            <Line label="Passages retrieved" value={String(answer.retrieval?.evidenceCount ?? 0)} />
            <Line
              label="Distinct sources"
              value={
                Number.isFinite(answer.distinctSourceCount)
                  ? String(answer.distinctSourceCount)
                  : null
              }
              testId="feedback-question-distinct-sources"
            />
            <Line
              label="Best match score"
              value={
                answer.retrieval?.topScore === null || answer.retrieval?.topScore === undefined
                  ? null
                  : String(answer.retrieval.topScore)
              }
            />
            <Line label="Answering provider" value={answer.provenance?.actualProvider} />
            <Line label="Provider asked for" value={answer.provenance?.requestedProvider} />
            <Line label="Model" value={answer.provenance?.modelId} />
            <Line
              label="Stand-in answered instead"
              value={fallbackTriState(answer.provenance)}
              testId="feedback-question-fallback"
            />
            <Line
              label="Grounded in demonstration content"
              value={demoGroundingTriState(answer.provenance)}
              testId="feedback-question-demo-grounding"
            />
          </div>

          {citations.length ? (
            <section data-testid="feedback-question-citations">
              <strong>Candidate passages PRIME found</strong>
              <ol className="feedback-ops-history-list">
                {citations.map((citation, index) => (
                  <li key={citation.citationId || `citation-${index}`}>
                    <strong>{citation.title || citation.source || citation.citationId}</strong>
                    {/* The score as PRIME recorded it, WITH where it came from —
                        a heuristic keyword score and a semantic score are not the
                        same number and must not read as one. */}
                    {citation.score === null || citation.score === undefined
                      ? ' · no score recorded'
                      : ` · score ${citation.score}${citation.scoreSource ? ` (${citation.scoreSource})` : ''}`}
                    {citation.excerpt ? <p className="muted">{citation.excerpt}</p> : null}
                  </li>
                ))}
              </ol>
            </section>
          ) : (
            <p className="muted" data-testid="feedback-question-no-citations">
              PRIME found no source material for this question.
            </p>
          )}

          {reasons.length ? (
            <section data-testid="feedback-question-reasons">
              <strong>Why PRIME would not stand behind its own answer</strong>
              <ul>
                {reasons.map((reason) => (
                  <li key={reason}>{describeQuestionDeflectionReason(reason)}</li>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      ) : (
        <p className="muted" data-testid="feedback-question-absent">
          This question carries no recorded answer attempt.
        </p>
      )}
    </article>
  );
}

/**
 * Every type-specific section for one record, or nothing at all.
 *
 * A screenshot-feedback record returns null here — the queue detail a reviewer
 * knew before commit 6 is exactly what it was.
 */
export function TypedIntakeDetailSections({ item, onFilterBySourceIntake }) {
  if (!item?.entryType || item.entryType === 'screen_feedback') return null;
  // Each section gets its OWN boundary: a crash in one vertical's stored data
  // must not take the sibling sections with it. Keyed by record id so selecting
  // a different record always re-attempts the render.
  const recordKey = item.id || item.sourceIntakeId || 'record';
  return (
    <>
      <TypedIntakeSectionBoundary key={`source-${recordKey}`}>
        <SourceIntakeSection item={item} onFilterBySourceIntake={onFilterBySourceIntake} />
      </TypedIntakeSectionBoundary>
      <TypedIntakeSectionBoundary key={`support-${recordKey}`}>
        <SupportTicketDetailSection item={item} />
      </TypedIntakeSectionBoundary>
      <TypedIntakeSectionBoundary key={`request-${recordKey}`}>
        <FeatureRequestDetailSection item={item} />
      </TypedIntakeSectionBoundary>
      <TypedIntakeSectionBoundary key={`question-${recordKey}`}>
        <QuestionDetailSection item={item} />
      </TypedIntakeSectionBoundary>
    </>
  );
}

export default TypedIntakeDetailSections;
